import { Modal } from '@/modules/common/components/Modal';
import { ApplicationValidationType } from '@/types/application';
import { UserRole } from '@/types/user';
import { Check } from '@mui/icons-material';
import { Button } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { CommentBox } from './CommentBox';
import { CourseCard } from './CourseCard';
import { ReviewModalProps } from './ReviewModal.types';
import { StudentOverviewRow } from './StudentOverviewRow';

type EducationProviderReviewModalProps = Omit<
  ReviewModalProps,
  'initialReview'
> & {
  validationsToReview: ApplicationValidationType[];
};

export function EducationProviderReviewModal({
  applicationsToReview,
  validationsToReview,
  userRoles,
  onClose,
  ...props
}: EducationProviderReviewModalProps) {
  const [application] = applicationsToReview ?? [];
  const [validationToReview] = validationsToReview ?? [];

  const user = application?.user;
  const courseCohorts = application?.course?.cohorts;
  const cohortId = application?.cohort;
  const selectedCohort = courseCohorts?.find(cohort => cohort._id === cohortId);
  const formMethods = useForm({
    defaultValues: { comments: '', [validationToReview]: false },
  });
  const { handleSubmit, setValue } = formMethods;

  if (!application || !validationToReview) {
    return null;
  }
  if (
    !user ||
    !userRoles.includes(UserRole.EducationProvider) ||
    !selectedCohort
  ) {
    throw new Error('Invalid application or validation');
  }

  const cohortStartDate = selectedCohort.startDate
    ? new Date(selectedCohort.startDate).toLocaleDateString()
    : 'TBD';

  const modalTitle =
    validationToReview === ApplicationValidationType.Skills
      ? "Please verify the user's skills for this application"
      : validationToReview === ApplicationValidationType.Capacity
        ? 'Please verify user enrollment capacity'
        : "Please verify the user's eligibility for admission";

  const modalDescription =
    validationToReview === ApplicationValidationType.Skills
      ? `By clicking 'Approve' will confirm the user's eligibility for the
          course automatically. If rejected, the user will be notified via email
          with additional information.`
      : validationToReview === ApplicationValidationType.Capacity
        ? `By clicking 'Approve' will confirm the user's enrollment in the ${application.course.name} starting ${cohortStartDate}. If rejected, the user will be notified via email with additional information.`
        : `By clicking 'Approve' will confirm the user's eligibility for admission. If rejected, the user will be notified via email with additional information.`;

  return (
    <Modal
      showCloseButton
      onClose={() => onClose()}
      {...props}
      title={modalTitle}
      subTitle={modalDescription}
      classes={{
        paper: 'max-w-3xl',
      }}
      actions={
        <div className="flex gap-3 justify-end">
          <Button
            variant="outlined"
            form="education-provider-review-form"
            type="submit"
            onClick={() => {
              setValue(validationToReview, false);
            }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            form="education-provider-review-form"
            type="submit"
            onClick={() => {
              setValue(validationToReview, true);
            }}
          >
            <div className="flex gap-2 items-center">
              Approve <Check />
            </div>
          </Button>
        </div>
      }
    >
      <FormProvider {...formMethods}>
        <form
          className="flex flex-col gap-4"
          id="education-provider-review-form"
          onSubmit={handleSubmit(onClose)}
        >
          <CourseCard
            course={application.course}
            selectedCohort={selectedCohort}
          />
          <StudentOverviewRow user={user} validationType={validationToReview} />
          <CommentBox />
        </form>
      </FormProvider>
    </Modal>
  );
}
